<template>
  <v-card color="grey lighten-5" flat class="report-table-card">
    <div class="report-topbar">
      <div class="report-meta">
        <span class="meta-item">
          Görünen Kolon:
          <strong>{{ visibleColumns.length }} / {{ internalHeaders.length }}</strong>
        </span>

        <span class="meta-item">
          Kayıt:
          <strong>{{ filteredItems.length }} / {{ normalizedItems.length }}</strong>
        </span>
      </div>

      <div class="report-actions">
        <v-text-field
          v-if="showSearch"
          flat
          solo-inverted
          hide-details
          v-model="internalSearch"
          :label="searchLabel"
          append-icon="search"
          class="toolbar-search"
        ></v-text-field>

        <slot name="actions"></slot>

        <v-btn color="info" @click="columnDialog = true">
          <v-icon left>view_column</v-icon>
          Kolonlar
        </v-btn>

        <v-btn
          v-if="showFrontendExport"
          color="success"
          @click="exportVisibleCsv"
        >
          <v-icon left>file_download</v-icon>
          Excel
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="columnDialog" max-width="760px" scrollable>
      <v-card>
        <v-card-title class="headline">
          Kolonlar
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-3">
          <div class="column-dialog-note">
            Kolon sırasını sürükle bırak ile değiştir, görünürlüğü açıp kapat.
          </div>

          <div class="column-list">
            <div
              v-for="(column, index) in internalHeaders"
              :key="column.value"
              class="column-item"
              draggable="true"
              @dragstart="onColumnDragStart(index)"
              @dragover.prevent
              @drop="onColumnDrop(index)"
            >
              <div class="column-item-left">
                <v-icon small class="mr-2 drag-handle">drag_indicator</v-icon>
                <span class="column-title">{{ column.text }}</span>
              </div>

              <v-switch
                v-model="column.visible"
                color="info"
                hide-details
                class="ma-0 pa-0 column-switch"
                @change="onColumnVisibilityChange"
              ></v-switch>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn flat color="warning" @click="resetColumns">
            Varsayılan Kolonlar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn flat color="blue darken-1" @click="columnDialog = false">
            Kapat
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="table-stage">
      <div
        class="report-scroll-top"
        ref="reportScrollTop"
        @scroll="syncTopScroll"
      >
        <div
          class="report-scroll-inner"
          ref="reportScrollInner"
        ></div>
      </div>

      <div
        ref="tableWrapper"
        class="table-wrapper"
        @scroll="syncBottomScroll"
      >
        <table ref="reportRealTable" class="modern-report-table">
          <colgroup>
            <col
              v-for="column in visibleColumns"
              :key="'col-' + column.value"
              :style="getColStyle(column)"
            />
          </colgroup>

          <thead>
            <tr>
              <th
                v-for="(header, index) in visibleColumns"
                :key="header.value"
                :class="[
                  'column-header',
                  header.align ? 'text-xs-' + header.align : 'text-xs-left',
                  {
                    sortable: header.sortable,
                    dragging: draggedHeaderIndex === index
                  }
                ]"
                :draggable="!isResizing"
                @dragstart="onHeaderDragStart($event, index)"
                @dragover.prevent
                @drop="onHeaderDrop(index)"
              >
                <div
                  class="header-content"
                  @click="onHeaderClick($event, header)"
                >
                  <div class="header-left">
                    <v-icon small class="drag-handle header-drag-handle">drag_indicator</v-icon>
                    <span class="header-text">{{ header.text }}</span>
                  </div>
                </div>

                <div v-if="header.sortable" class="sort-icons">
                  <v-icon small v-if="sortBy === header.value && sortDesc">arrow_downward</v-icon>
                  <v-icon small v-else-if="sortBy === header.value && !sortDesc">arrow_upward</v-icon>
                  <v-icon small v-else>unfold_more</v-icon>
                </div>

                <div
                  class="column-resizer"
                  @mousedown.stop.prevent="startResize($event, header)"
                  @click.stop.prevent
                ></div>
              </th>
            </tr>
          </thead>

          <tbody v-if="paginatedItems.length">
            <tr
              v-for="(item, rowIndex) in paginatedItems"
              :key="getRowKey(item, rowIndex)"
              :class="getRowClass(item)"
            >
              <td
                v-for="column in visibleColumns"
                :key="column.value"
                :class="[
                  'report-cell',
                  column.align ? 'text-xs-' + column.align : 'text-xs-left'
                ]"
              >
                <template v-if="$scopedSlots.cell">
                  <slot
                    name="cell"
                    :item="item"
                    :column="column"
                    :value="formatCellValue(item, column)"
                  >
                    {{ formatCellValue(item, column) }}
                  </slot>
                </template>

                <template v-else>
                  <div class="cell-content">
                    {{ formatCellValue(item, column) }}
                  </div>
                </template>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td :colspan="Math.max(visibleColumns.length, 1)" class="empty-state-cell">
                <div
                  v-if="hasSearched && !loading && filteredItems.length === 0"
                  class="empty-state"
                >
                  Kayıt bulunamadı
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="custom-pagination">
      <div class="pagination-left">
        <span>Sayfa başına:</span>
        <v-select
          :items="rowsPerPageItems"
          v-model="rowsPerPage"
          hide-details
          solo
          flat
          class="rows-per-page-select"
        ></v-select>
      </div>

      <div class="pagination-center">
        Toplam kayıt: <strong>{{ filteredItems.length }}</strong>
      </div>

      <div class="pagination-right">
        <v-btn icon :disabled="page <= 1" @click="goToFirstPage">
          <v-icon>first_page</v-icon>
        </v-btn>

        <v-btn icon :disabled="page <= 1" @click="page = page - 1">
          <v-icon>chevron_left</v-icon>
        </v-btn>

        <span class="page-info">
          Sayfa {{ safePage }} / {{ totalPages }}
        </span>

        <v-btn icon :disabled="page >= totalPages" @click="page = page + 1">
          <v-icon>chevron_right</v-icon>
        </v-btn>

        <v-btn icon :disabled="page >= totalPages" @click="goToLastPage">
          <v-icon>last_page</v-icon>
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import helpers from "@/components/app/customcomp/helpers.js";

export default {
  name: "ReportTable",

  props: {
    headers: {
      type: Array,
      default: function () {
        return [];
      }
    },
    items: {
      type: Array,
      default: function () {
        return [];
      }
    },
    storageKey: {
      type: String,
      default: "report_table_default"
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasSearched: {
      type: Boolean,
      default: false
    },
    searchLabel: {
      type: String,
      default: "Ara"
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showFrontendExport: {
      type: Boolean,
      default: true
    },
    rowsPerPageItems: {
      type: Array,
      default: function () {
        return [10, 20, 50, 100, 250, 500, 1000];
      }
    },
    rowClassFn: {
      type: Function,
      default: null
    },
    excelSummaryConfig: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      internalHeaders: [],
      internalSearch: "",
      columnDialog: false,
      sortBy: "",
      sortDesc: false,
      page: 1,
      rowsPerPage: 20,
      draggedColumnIndex: null,
      draggedHeaderIndex: null,
      defaultHeadersSnapshot: [],
      isResizing: false,
      resizeState: {
        active: false,
        columnValue: "",
        startX: 0,
        startWidth: 0
      }
    };
  },

  computed: {
    normalizedItems() {
      return Array.isArray(this.items) ? this.items : [];
    },

    visibleColumns() {
      return this.internalHeaders.filter(function (x) {
        return x.visible;
      });
    },

    filteredItems() {
      const vm = this;
      let list = this.normalizedItems.slice();

      if (this.internalSearch && String(this.internalSearch).trim() !== "") {
        const keyword = String(this.internalSearch).toLocaleLowerCase("tr-TR").trim();

        list = list.filter(function (item) {
          return vm.internalHeaders.some(function (column) {
            const raw = vm.getExportValue(item, column);
            return String(raw || "")
              .toLocaleLowerCase("tr-TR")
              .indexOf(keyword) > -1;
          });
        });
      }

      if (this.sortBy) {
        list.sort(this.sortFunction);
      }

      return list;
    },

    totalPages() {
      if (!this.filteredItems.length) {
        return 1;
      }

      return Math.max(1, Math.ceil(this.filteredItems.length / this.rowsPerPage));
    },

    safePage() {
      return Math.min(this.page, this.totalPages);
    },

    paginatedItems() {
      const start = (this.safePage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.filteredItems.slice(start, end);
    }
  },

  watch: {
    headers: {
      handler(newVal) {
        this.setupHeaders(newVal);
      },
      deep: true,
      immediate: true
    },

    filteredItems() {
      if (this.page > this.totalPages) {
        this.page = this.totalPages;
      }

      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    rowsPerPage() {
      this.page = 1;
      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    internalSearch(val) {
      this.$emit("search-change", val);
    },

    paginatedItems() {
      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    visibleColumns() {
      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    columnDialog(val) {
      if (!val) {
        this.$nextTick(() => {
          this.updateTopScrollbar();
        });
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.updateTopScrollbar();
    });

    window.addEventListener("resize", this.updateTopScrollbar);
    document.addEventListener("mousemove", this.onResizeMouseMove);
    document.addEventListener("mouseup", this.stopResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateTopScrollbar);
    document.removeEventListener("mousemove", this.onResizeMouseMove);
    document.removeEventListener("mouseup", this.stopResize);
  },

  methods: {
    setupHeaders(headers) {
      const mapped = (headers || []).map(function (item) {
        const incomingWidth =
          item && item.width !== undefined && item.width !== null
            ? item.width
            : "140px";

        return Object.assign(
          {
            visible: true,
            sortable: true,
            type: "text",
            align: "left",
            minWidth: "1px",
            width: incomingWidth
          },
          item,
          {
            minWidth: "1px"
          }
        );
      });

      this.defaultHeadersSnapshot = JSON.parse(JSON.stringify(mapped));
      this.internalHeaders = mapped;
      this.loadColumnSettings();

      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    syncTopScroll(e) {
      if (this.$refs.tableWrapper) {
        this.$refs.tableWrapper.scrollLeft = e.target.scrollLeft;
      }
    },

    syncBottomScroll(e) {
      if (this.$refs.reportScrollTop) {
        this.$refs.reportScrollTop.scrollLeft = e.target.scrollLeft;
      }
    },

    updateTopScrollbar() {
      this.$nextTick(() => {
        const table = this.$refs.reportRealTable;
        if (!table || !this.$refs.reportScrollInner) {
          return;
        }

        this.$refs.reportScrollInner.style.width = table.scrollWidth + "px";
      });
    },

    onColumnVisibilityChange() {
      this.saveColumnSettings();
      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    isEmptyValue(val) {
      return val === null || val === undefined || val === "";
    },

    formatCellValue(item, column) {
      if (!item || !column) {
        return "";
      }

      const value = item[column.value];

      if (column.type === "date") {
        return this.isEmptyValue(value) ? "" : helpers.dateformat(value);
      }

      if (column.type === "money") {
        return this.isEmptyValue(value) ? "" : helpers.moneyformat(value);
      }

      if (column.type === "balance") {
        return this.isEmptyValue(value) ? "" : this.formatBalance(value);
      }

      return this.isEmptyValue(value) ? "" : value;
    },

    formatBalance(val) {
      if (val > 0) {
        return helpers.moneyformat(val) + " B";
      } else if (val == 0) {
        return helpers.moneyformat(val);
      } else {
        return helpers.moneyformat(val * -1) + " A";
      }
    },

    getExportValue(item, column) {
      return this.formatCellValue(item, column);
    },

    getRawNumberValue(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      if (typeof value === "number") {
        return isNaN(value) ? 0 : value;
      }

      const normalized = String(value)
        .replace(/\s/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".");

      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? 0 : parsed;
    },

    calculateSummaryTotals() {
      const config = this.excelSummaryConfig;

      if (
        !config ||
        !Array.isArray(config.fields) ||
        !config.fields.length
      ) {
        return {};
      }

      const totals = {};

      config.fields.forEach(function (field) {
        if (field && field.key) {
          totals[field.key] = 0;
        }
      });

      this.filteredItems.forEach((item) => {
        config.fields.forEach((field) => {
          if (!field || !field.key) {
            return;
          }

          totals[field.key] += this.getRawNumberValue(item[field.key]);
        });
      });

      return totals;
    },

    buildSummaryRow(exportColumns) {
      const config = this.excelSummaryConfig;

      if (
        !config ||
        !Array.isArray(config.fields) ||
        !config.fields.length ||
        !Array.isArray(exportColumns) ||
        !exportColumns.length
      ) {
        return null;
      }

      const totals = this.calculateSummaryTotals();
      const firstVisibleColumnValue = exportColumns[0].value;
      const label = config.label || "Genel Toplam";

      return exportColumns.map((column) => {
        if (column.value === firstVisibleColumnValue) {
          return label;
        }

        const fieldConfig = config.fields.find(function (field) {
          return field && field.key === column.value;
        });

        if (!fieldConfig) {
          return "";
        }

        const totalValue = totals[column.value] || 0;

        if (fieldConfig.type === "money" || column.type === "money") {
          return helpers.moneyformat(totalValue);
        }

        return totalValue;
      });
    },

    csvEscape(value) {
      const stringValue = String(value == null ? "" : value).replace(/"/g, '""');
      return '"' + stringValue + '"';
    },

    exportVisibleCsv() {
      const exportColumns = this.visibleColumns;
      const exportRows = this.filteredItems;

      if (!exportColumns.length) {
        return;
      }

      const headerRow = exportColumns
        .map((column) => this.csvEscape(column.text))
        .join(";");

      const bodyRows = exportRows.map((item) => {
        return exportColumns
          .map((column) => this.csvEscape(this.getExportValue(item, column)))
          .join(";");
      });

      const summaryRowValues = this.buildSummaryRow(exportColumns);

      if (summaryRowValues && summaryRowValues.length) {
        bodyRows.push(
          summaryRowValues.map((value) => this.csvEscape(value)).join(";")
        );
      }

      const csvContent = "\uFEFF" + [headerRow].concat(bodyRows).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);

      link.href = url;
      link.download = this.storageKey + ".csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    onHeaderClick(event, header) {
      if (this.isResizing) {
        return;
      }

      if (
        event &&
        event.target &&
        event.target.closest &&
        event.target.closest(".column-resizer")
      ) {
        return;
      }

      if (header.sortable) {
        this.toggleSort(header.value);
      }
    },

    toggleSort(columnValue) {
      if (this.sortBy === columnValue) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = columnValue;
        this.sortDesc = false;
      }

      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    sortFunction(a, b) {
      const column = this.internalHeaders.find((x) => x.value === this.sortBy);
      if (!column) {
        return 0;
      }

      const aVal = a ? a[column.value] : null;
      const bVal = b ? b[column.value] : null;

      let result = 0;

      if (column.type === "money" || column.type === "balance") {
        const aNum = parseFloat(aVal) || 0;
        const bNum = parseFloat(bVal) || 0;
        result = aNum === bNum ? 0 : aNum > bNum ? 1 : -1;
      } else if (column.type === "date") {
        const aDate = aVal ? new Date(aVal).getTime() : 0;
        const bDate = bVal ? new Date(bVal).getTime() : 0;
        result = aDate === bDate ? 0 : aDate > bDate ? 1 : -1;
      } else {
        const aText = String(aVal == null ? "" : aVal).toLocaleLowerCase("tr-TR");
        const bText = String(bVal == null ? "" : bVal).toLocaleLowerCase("tr-TR");
        result = aText.localeCompare(bText, "tr");
      }

      return this.sortDesc ? result * -1 : result;
    },

    goToFirstPage() {
      this.page = 1;
    },

    goToLastPage() {
      this.page = this.totalPages;
    },

    onColumnDragStart(index) {
      this.draggedColumnIndex = index;
    },

    onColumnDrop(index) {
      if (this.draggedColumnIndex === null || this.draggedColumnIndex === index) {
        this.draggedColumnIndex = null;
        return;
      }

      const sourceColumns = this.internalHeaders.slice();
      const movedItem = sourceColumns.splice(this.draggedColumnIndex, 1)[0];
      sourceColumns.splice(index, 0, movedItem);

      this.internalHeaders = sourceColumns;
      this.draggedColumnIndex = null;
      this.saveColumnSettings();
      this.updateTopScrollbar();
    },

    onHeaderDragStart(event, index) {
      if (this.isResizing) {
        event.preventDefault();
        return;
      }

      if (
        event &&
        event.target &&
        event.target.closest &&
        event.target.closest(".column-resizer")
      ) {
        event.preventDefault();
        return;
      }

      this.draggedHeaderIndex = index;
    },

    onHeaderDrop(targetVisibleIndex) {
      if (this.isResizing) {
        this.draggedHeaderIndex = null;
        return;
      }

      if (
        this.draggedHeaderIndex === null ||
        this.draggedHeaderIndex === targetVisibleIndex
      ) {
        this.draggedHeaderIndex = null;
        return;
      }

      const visible = this.visibleColumns.slice();
      const draggedVisible = visible[this.draggedHeaderIndex];
      const targetVisible = visible[targetVisibleIndex];

      if (!draggedVisible || !targetVisible) {
        this.draggedHeaderIndex = null;
        return;
      }

      const sourceIndex = this.internalHeaders.findIndex((x) => x.value === draggedVisible.value);
      const targetIndex = this.internalHeaders.findIndex((x) => x.value === targetVisible.value);

      if (sourceIndex === -1 || targetIndex === -1) {
        this.draggedHeaderIndex = null;
        return;
      }

      const updated = this.internalHeaders.slice();
      const moved = updated.splice(sourceIndex, 1)[0];
      updated.splice(targetIndex, 0, moved);
      this.internalHeaders = updated;

      this.draggedHeaderIndex = null;
      this.saveColumnSettings();
      this.updateTopScrollbar();
    },

    parseWidth(value, fallback) {
      if (typeof value === "number" && !isNaN(value)) {
        return value;
      }

      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? fallback : parsed;
    },

    getColStyle(column) {
      const width = this.parseWidth(column.width || column.minWidth, 140);
      const minWidth = 1;

      return {
        width: width + "px",
        minWidth: minWidth + "px",
        maxWidth: width + "px"
      };
    },

    startResize(event, column) {
      if (!column || !column.value) {
        return;
      }

      this.isResizing = true;
      this.draggedHeaderIndex = null;

      this.resizeState = {
        active: true,
        columnValue: column.value,
        startX: event.clientX,
        startWidth: this.parseWidth(column.width || column.minWidth, 140)
      };

      document.body.classList.add("report-table-resizing");
    },

    onResizeMouseMove(event) {
      if (!this.resizeState.active) {
        return;
      }

      const columnIndex = this.internalHeaders.findIndex(function (x) {
        return x.value === this.resizeState.columnValue;
      }, this);

      if (columnIndex === -1) {
        return;
      }

      const currentColumn = this.internalHeaders[columnIndex];
      const minWidth = 1;
      const delta = event.clientX - this.resizeState.startX;
      const nextWidth = Math.max(minWidth, this.resizeState.startWidth + delta);

      const updated = this.internalHeaders.slice();
      updated[columnIndex] = Object.assign({}, updated[columnIndex], {
        width: nextWidth + "px",
        minWidth: "1px"
      });

      this.internalHeaders = updated;

      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    stopResize() {
      if (!this.resizeState.active) {
        return;
      }

      this.resizeState = {
        active: false,
        columnValue: "",
        startX: 0,
        startWidth: 0
      };

      this.isResizing = false;
      document.body.classList.remove("report-table-resizing");

      this.saveColumnSettings();

      this.$nextTick(() => {
        this.updateTopScrollbar();
      });
    },

    saveColumnSettings() {
      try {
        const payload = this.internalHeaders.map(function (column) {
          return {
            text: column.text,
            value: column.value,
            visible: column.visible,
            width: column.width || "",
            minWidth: "1px"
          };
        });

        window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
      } catch (e) {
        // ignore
      }
    },

    loadColumnSettings() {
      try {
        const saved = window.localStorage.getItem(this.storageKey);
        if (!saved) {
          return;
        }

        const parsed = JSON.parse(saved);
        if (!Array.isArray(parsed) || !parsed.length) {
          return;
        }

        const mapped = [];
        parsed.forEach((savedColumn) => {
          const original = this.internalHeaders.find((x) => x.value === savedColumn.value);
          if (original) {
            mapped.push(
              Object.assign({}, original, {
                visible:
                  typeof savedColumn.visible === "boolean"
                    ? savedColumn.visible
                    : original.visible,
                width: savedColumn.width || original.width,
                minWidth: "1px"
              })
            );
          }
        });

        this.internalHeaders.forEach((column) => {
          if (!mapped.find((x) => x.value === column.value)) {
            mapped.push(
              Object.assign({}, column, {
                minWidth: "1px"
              })
            );
          }
        });

        this.internalHeaders = mapped;
      } catch (e) {
        // ignore
      }
    },

    resetColumns() {
      const reset = JSON.parse(JSON.stringify(this.defaultHeadersSnapshot)).map(function (column) {
        return Object.assign({}, column, {
          minWidth: "1px"
        });
      });

      this.internalHeaders = reset;
      this.saveColumnSettings();
      this.updateTopScrollbar();
    },

    getRowClass(item) {
      if (typeof this.rowClassFn === "function") {
        return this.rowClassFn(item);
      }

      return "";
    },

    getRowKey(item, index) {
      if (item && (item.id || item.ID || item.Id)) {
        return item.id || item.ID || item.Id;
      }

      if (item && item.PNR) {
        return item.PNR + "_" + index;
      }

      return index;
    }
  }
};
</script>

<style scoped>
.report-table-card {
  border-radius: 12px;
  overflow: visible;
}

.report-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 12px 0;
  flex-wrap: wrap;
  gap: 12px;
}

.report-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  color: #78909c;
  font-size: 14px;
}

.meta-item strong {
  color: #37474f;
}

.report-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.toolbar-search {
  max-width: 280px;
}

.table-stage {
  position: relative;
  overflow: visible;
}

.report-scroll-top {
  overflow-x: auto;
  overflow-y: hidden;
  height: 12px;
  margin-bottom: 8px;
}

.report-scroll-inner {
  height: 1px;
}

.table-wrapper {
  width: 100%;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  background: #fff;
  overflow: auto;
}

.modern-report-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.column-header {
  position: relative;
  background: #f8fafc;
  border-bottom: 1px solid #e6ebf2 !important;
  border-right: 1px solid #eef2f7;
  user-select: none;
  vertical-align: top;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  overflow: hidden;
  padding: 6px 22px 6px 4px;
  min-width: 0;
  max-width: 1px;
}

.column-header.sortable {
  cursor: pointer;
}

.header-content {
  display: block;
  min-height: 16px;
  min-width: 0;
  width: 100%;
  overflow: hidden;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  width: 100%;
  overflow: hidden;
}

.header-text {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  font-weight: 600;
  color: #37474f;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.2;
  overflow: hidden;
}

.sort-icons {
  position: absolute;
  top: 6px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1976d2;
  width: 12px;
  pointer-events: none;
}

.drag-handle {
  cursor: move;
  color: #90a4ae;
  font-size: 14px !important;
}

.header-drag-handle {
  flex: 0 0 12px;
  width: 12px;
  min-width: 12px;
  margin-top: 1px;
  margin-right: 2px;
}

.column-resizer {
  position: absolute;
  top: 0;
  right: -2px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 6;
}

.column-resizer::after {
  content: "";
  position: absolute;
  top: 15%;
  right: 3px;
  width: 2px;
  height: 70%;
  background: #cfd8dc;
  border-radius: 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.column-header:hover .column-resizer::after,
.column-resizer:hover::after {
  opacity: 1;
}

.report-cell {
  border-bottom: 1px solid #edf2f7;
  border-right: 1px solid #f5f7fa;
  padding: 8px 4px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  overflow: hidden;
  vertical-align: top;
  line-height: 1.45;
  background: #fff;
  min-width: 0;
  max-width: 1px;
}

.cell-content {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.empty-state-cell {
  padding: 0;
}

.empty-state {
  padding: 24px 12px;
  text-align: center;
  color: #78909c;
  font-weight: 600;
}

.custom-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 4px 4px 4px;
  flex-wrap: wrap;
}

.pagination-left,
.pagination-center,
.pagination-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rows-per-page-select {
  width: 110px;
}

.page-info {
  min-width: 110px;
  text-align: center;
  font-weight: 600;
  color: #546e7a;
}

.column-dialog-note {
  color: #90a4ae;
  margin-bottom: 14px;
  font-size: 13px;
}

.column-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.column-item {
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fafbfd;
}

.column-item-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.column-title {
  font-weight: 600;
  color: #455a64;
}

.column-switch {
  margin-left: 12px;
}

.report-scroll-top::-webkit-scrollbar,
.table-wrapper::-webkit-scrollbar {
  height: 12px;
}

.report-scroll-top::-webkit-scrollbar-track,
.table-wrapper::-webkit-scrollbar-track {
  background: #edf2f7;
  border-radius: 10px;
}

.report-scroll-top::-webkit-scrollbar-thumb,
.table-wrapper::-webkit-scrollbar-thumb {
  background: #9e9e9e;
  border-radius: 10px;
}

.report-scroll-top::-webkit-scrollbar-thumb:hover,
.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #7f8c8d;
}

@media (max-width: 960px) {
  .report-topbar {
    flex-direction: column;
    align-items: stretch;
  }

  .report-actions {
    justify-content: flex-start;
  }

  .custom-pagination {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar-search {
    max-width: 100%;
  }
}
</style>

<style>
body.report-table-resizing {
  cursor: col-resize !important;
  user-select: none !important;
}
</style>
